import { Box, Button, InputLabel, Stack, Typography } from "@mui/material";
import ModalContainer from "../UIElements/modal-container";
import { useTheme } from "@mui/material/styles";
import { useNotificationService } from "../notification";
import { useAuth } from "../../hooks/useAuth";
import { UserRoleData } from "../../interface/users";
import { labelSty, labelStyles, textSty } from "../UIElements/styles-input";
import useResponsive from "../../hooks/useResponsive";
import { headerSty } from "../table-styles";
import {
  formatDateFromString,
  formatTextType,
  limitToSeven,
} from "../../utils/url-id";
import { LoadingButton } from "@mui/lab";
import { apiDeleteRole, apiRemovePermissionFromRole } from "../../api/access";
import { useMutation } from "@tanstack/react-query";
import CancelIcon from "@mui/icons-material/Cancel";
import { FC, useState } from "react";
import { EntryValue } from "./PermissionsDetailsContent";
import { InputCustomSize } from "../UIElements/input-ui";
import SelectInput from "../UIElements/select";
import { apiCreateAdmin, CreateAdminProps } from "../../api/users";

export const RolesContentModal = ({
  open,
  modalHandler,
  roleDetails,
  handleRefresh,
}: {
  open: boolean;
  modalHandler: () => void;
  roleDetails: UserRoleData;
  handleRefresh: () => void;
}) => {
  const theme = useTheme();
  const { showNotification } = useNotificationService();
  const isMedium = useResponsive("down", "md", "xl");
  // const [selected_permissions, setSelectedPermissions] = useState<EntryValue[]>(
  //   []
  // );

  const mutation = useMutation({
    mutationFn: () => apiDeleteRole(roleDetails._id),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        showNotification(data?.data?.message ?? "Success");
        handleRefresh();
        modalHandler();
      }
    },
    onError: (error) => {
      console.error("Fetch process failed", error);
      showNotification("Fetch process failed", "error");
    },
  });
  const remove_mutation = useMutation({
    mutationFn: (data: EntryValue[]) =>
      apiRemovePermissionFromRole(roleDetails._id, data),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        showNotification(data?.data?.message ?? "Success");
      }
    },
    onError: (error) => {
      console.error("Fetch process failed", error);
      showNotification("Fetch process failed", "error");
    },
  });

  const handleDeleteRole = () => {
    mutation.mutate();
  };

  const handlePermissionsDelete = (permission: EntryValue) => {
    remove_mutation.mutate([permission]);
  };

  return (
    <ModalContainer
      sx={{
        borderRadius: "16px",
      }}
      size={"sm"}
      openModal={open}
      fullWidth={true}
      handleModal={modalHandler}
    >
      <Box sx={{ background: "#fff", padding: 4, borderRadius: "16px" }}>
        <Box>
          <Typography
            sx={{ ...headerSty, textAlign: "left", my: 2, color: "black" }}
            variant="subtitle2"
          >
            Role Information
          </Typography>
          <Box>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>Name</Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(roleDetails?.name)}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Description
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatTextType(roleDetails?.description)}
                </Typography>
              </Stack>
            </Stack>
          </Box>

          {/*  */}
          <Box>
            <Typography
              sx={{
                ...headerSty,
                textAlign: "left",
                my: 0,
                mb: 2,
                color: "black",
              }}
              variant="subtitle2"
            >
              User Information
            </Typography>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>Name</Typography>
                <Typography sx={{ ...textSty }}>
                  {roleDetails?.user_info?.name}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>Email</Typography>
                <Typography sx={{ ...textSty }}>
                  {roleDetails?.user_info?.email}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>User Id</Typography>
                <Typography sx={{ ...textSty }}>
                  {limitToSeven(roleDetails?.user_info?._id)}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...labelStyles, mb: 0 }}>
                  Created At
                </Typography>
                <Typography sx={{ ...textSty }}>
                  {formatDateFromString(roleDetails?.created_at)}
                </Typography>
              </Stack>
            </Stack>
          </Box>

          {roleDetails?.permissions?.length > 0 && (
            <Stack>
              <Stack>
                <Typography sx={{ ...headerSty, textAlign: "start" }}>
                  Role Description
                </Typography>
              </Stack>
              {roleDetails?.permissions?.map((val, index) => (
                <Stack
                  key={index}
                  direction={"row"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  <Typography sx={{ ...labelSty }}>
                    {val?.description}
                  </Typography>
                  <Button
                    onClick={() => handlePermissionsDelete(val)}
                    color="error"
                    endIcon={<CancelIcon />}
                    sx={{ width: 100 }}
                  >
                    Remove
                  </Button>
                </Stack>
              ))}
            </Stack>
          )}
        </Box>
        <Stack
          direction={"row"}
          justifyContent={"space-around"}
          sx={{ gap: 3, mt: 3 }}
        >
          <Button
            onClick={modalHandler}
            sx={{
              width: 150,
              borderRadius: "4px",
              background: theme.palette.lightBlue.light,
            }}
            variant="outlined"
          >
            Close
          </Button>
          <LoadingButton
            onClick={handleDeleteRole}
            color="error"
            loading={mutation.isPending}
            variant="contained"
            sx={{
              width: 150,
              borderRadius: "4px",
            }}
          >
            Delete Role
          </LoadingButton>
        </Stack>
      </Box>
    </ModalContainer>
  );
};

export const CreateUsers: FC<{ open: boolean; handleModal: () => void }> = ({
  open,
  handleModal,
}) => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");
  const { token } = useAuth();
  const { showNotification } = useNotificationService();
  const [user_data, setuser_data] = useState({
    first_name: "",
    last_name: "",
    phone_number: "",
    password: "",
    middle_name: "",
    email: "",
    gender: "",
  });

  const role_mutation = useMutation({
    mutationFn: (field: CreateAdminProps) => apiCreateAdmin(field, token),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        showNotification(data?.data?.message ?? "success");
        handleModal();
      }
    },
    onError: (error) => {
      console.error("Fetch process failed", error);
      showNotification("Fetch process failed", "error");
    },
  });
  const handleSubmit = () => {
    role_mutation.mutate(user_data);
  };
  return (
    <ModalContainer
      sx={{
        borderRadius: "16px",
      }}
      size={"md"}
      openModal={open}
      fullWidth={true}
      handleModal={handleModal}
    >
      <Box sx={{ background: "#fff", padding: 4, borderRadius: "16px" }}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: "Outfit",
            fontWeight: 500,
            color: "#292925",
          }}
        >
          Create User
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{
            fontFamily: "Outfit",
            fontWeight: 500,
            color: "#292925",
          }}
        >
          Kindly fill the form below to create a new user then assign a role to
          the user.
        </Typography>
        <Box component={"form"}>
          <Stack
            direction={isMedium ? "column" : "row"}
            alignItems={"center"}
            justifyContent={"space-between"}
            sx={{ my: 2, gap: 2 }}
          >
            <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
              <InputLabel sx={{ ...labelSty }}>First name</InputLabel>
              <InputCustomSize
                size={"large"}
                placeholder={""}
                value={user_data.first_name}
                onchange={(e: any) =>
                  setuser_data((prev) => ({
                    ...prev,
                    first_name: e.target.value,
                  }))
                }
                type={"text"}
                styles={undefined}
              />
            </Stack>
            <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
              <InputLabel sx={{ ...labelSty }}>Last name</InputLabel>
              <InputCustomSize
                size={"large"}
                placeholder={""}
                value={user_data.last_name}
                onchange={(e: any) =>
                  setuser_data((prev) => ({
                    ...prev,
                    last_name: e.target.value,
                  }))
                }
                type={"text"}
                styles={{}}
              />
            </Stack>
          </Stack>
          <Stack
            direction={isMedium ? "column" : "row"}
            alignItems={"center"}
            justifyContent={"space-between"}
            sx={{ my: 3, gap: 2 }}
          >
            <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
              <InputLabel sx={{ ...labelSty }}>Phone number</InputLabel>
              <InputCustomSize
                size={"large"}
                placeholder={""}
                value={user_data.phone_number}
                onchange={(e: any) =>
                  setuser_data((prev) => ({
                    ...prev,
                    phone_number: e.target.value,
                  }))
                }
                type={"text"}
                styles={undefined}
              />
            </Stack>
            <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
              <InputLabel sx={{ ...labelSty }}>Email</InputLabel>
              <InputCustomSize
                placeholder={""}
                size={"large"}
                value={user_data.email}
                onchange={(e: any) =>
                  setuser_data((prev) => ({
                    ...prev,
                    email: e.target.value,
                  }))
                }
                type={"email"}
                styles={undefined}
              />
            </Stack>
          </Stack>
          <Stack
            direction={isMedium ? "column" : "row"}
            alignItems={"center"}
            justifyContent={"space-between"}
            sx={{ my: 3, gap: 2 }}
          >
            <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
              <InputLabel sx={{ ...labelSty }}>Password</InputLabel>
              <InputCustomSize
                placeholder={""}
                size={"large"}
                value={user_data.password}
                onchange={(e: any) =>
                  setuser_data((prev) => ({
                    ...prev,
                    password: e.target.value,
                  }))
                }
                type={"text"}
                styles={{}}
              />
            </Stack>
            <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
              <SelectInput
                label="Gender"
                value={user_data.gender}
                onChange={(e: any) =>
                  setuser_data((prev) => ({
                    ...prev,
                    gender: e.target.value,
                  }))
                }
                options={[
                  { value: "", label: "Select Gender" },
                  { value: "male", label: "Male" },
                  { value: "female", label: "Female" },
                ]}
              />
            </Stack>
          </Stack>

          <Stack
            direction={"row"}
            justifyContent={"space-around"}
            sx={{ gap: 3 }}
          >
            <Button
              onClick={handleModal}
              sx={{
                width: 150,
                fontWeight: 700,
                borderRadius: "12px",
                background: theme.palette.lightBlue.light,
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <LoadingButton
              loading={role_mutation.isPending}
              onClick={handleSubmit}
              sx={{ width: 170, borderRadius: "12px", fontWeight: 700 }}
              variant="contained"
            >
              Create User
            </LoadingButton>
          </Stack>
        </Box>
      </Box>
    </ModalContainer>
  );
};
